//* External
import { isArray, pickBy } from 'lodash';
import moment from 'moment-timezone';

//* App Custom
import { tnl } from '@i18nApp/i18n';
import { toast } from 'react-toastify';
import sendRequest from './sendRequest';

const DATE_FORMATS = {
  DAY: 'DD/MM/YYYY',
  HOUR: 'h:mm A',
  FULL_DATE: 'DD/MM/YYYY hh:mm A'
};
export const REMOVE_FILTER_SELECT = {
  label: 'components.input_select_remove_filter',
  value: ''
};

export const EMPTY_SELECT = {
  label: 'components.input_select_empty',
  value: '-'
};

const formatDate = (date, format = DATE_FORMATS.DAY) => {
  return date
    ? moment(date)
        .tz(import.meta.env.VITE_TIME_ZONE)
        .format(format)
    : '';
};

const calculateAge = (date) => {
  if (date) return moment().diff(date, 'years');
};

const formatDateForInput = (date, format = DATE_FORMATS.DAY) => {
  const isDate = typeof date === 'string' ? new Date(date) : date;
  let dateToReturn = '';
  if (!isNaN(isDate)) {
    dateToReturn = moment(isDate)
      .tz(import.meta.env.VITE_TIME_ZONE)
      .format();
    if (format === DATE_FORMATS.DAY) {
      const [day] = dateToReturn.split('T');
      dateToReturn = day;
    } else if (format === DATE_FORMATS.FULL_DATE) {
      dateToReturn = dateToReturn.slice(0, -9);
    }
  }
  return dateToReturn;
};

const formatDateForInputString = (date, format = DATE_FORMATS.DAY) => {
  const isDate = typeof date === 'string' ? date : NaN;
  let dateToReturn = '';
  dateToReturn = moment(isDate)
    .tz(import.meta.env.VITE_TIME_ZONE)
    .format();
  if (format === DATE_FORMATS.DAY) {
    const [day] = dateToReturn.split('T');
    dateToReturn = day;
  } else if (format === DATE_FORMATS.FULL_DATE) {
    dateToReturn = dateToReturn.slice(0, -9);
  }
  return dateToReturn;
};

const isAllDataNull = (arrayData) =>
  arrayData.every((data) => data === null || data === undefined || data === '');

const verifyData = (arrayData, separator = ' ') => {
  if (isAllDataNull(arrayData)) return ' - ';
  else return arrayData.join(separator);
};

const cleanObject = (data) =>
  pickBy(data, (value) => {
    if (isArray(value) && value?.length !== 0) return true;
    if (!isArray(value) && !!value) return true;
    return false;
  });

const cleanArray = (data) => data.filter((el) => el != null);

const cleanArrayOfObjects = (data) =>
  data?.filter((obj) => Object.values(obj).every((el) => !!el));

const turnIntoFormData = (data, multipleName = '') => {
  let formData = new FormData();
  if (!Array.isArray(data)) {
    Object.entries(data).forEach((entry) => {
      if (entry[1]) {
        formData.append(entry[0], entry[1]);
      }
    });
  } else {
    data.map((item, index) => {
      Object.entries(item).forEach((entry) => {
        if (entry[1]) {
          formData.append(`${multipleName}[${index}]${entry[0]}`, entry[1]);
        }
      });
    });
  }
  return formData;
};

const createDropdownOptions = (array) =>
  array.map((item) => ({
    label: tnl(item),
    value: item
  }));

const newApplicationFromFrontend = (application) => {
  if (typeof application.pk === 'string') {
    return application.pk.split('_')[0];
  } else return null;
};

const calculateColumnsWidth = ({
  componentsAdded,
  space = 80,
  width = 150
}) => {
  const newWidth = space * componentsAdded + width;
  return `${newWidth}px`;
};

const downloadServerFile = (response, filename) => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const redirectServerFile = (response) => {
  const link = document.createElement('a');
  link.target = '_blank';
  window.open(response.data.report_file);
};

export const obtainParams = (cleanData, mode) => {
  return Object.fromEntries(
    Object.entries(cleanData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [key, value.map((item) => item[mode]).join(',')];
      } else if (
        typeof value === 'object' &&
        value !== null &&
        'label' in value &&
        'value' in value
      ) {
        return [key, value[mode]];
      } else {
        return [key, value];
      }
    })
  );
};

// getFormattedDefaultValue Implementado para Ufcas
const getFormattedDefaultValue = (defaultValue, inputType, isMultiple) => {
  if (!defaultValue) return null;
  switch (inputType) {
    case 'date':
    case 'datetime-local': {
      const dateToFormat = {
        date: DATE_FORMATS.DAY,
        'datetime-local': DATE_FORMATS.FULL_DATE
      }[inputType];
      return formatDateForInputString(defaultValue, dateToFormat);
    }
    case 'select': {
      if (isMultiple) {
        return createDropdownOptions(defaultValue?.split(',') || []);
      }
      return { label: defaultValue, value: defaultValue };
    }
    default:
      return defaultValue;
  }
};

export const presignedFile = async (url, file, setLoadingPresigned) => {
  if (!file) return null;
  setLoadingPresigned?.(true);
  let key = '';
  const formData = new FormData();
  formData.append('s3_key', file.name);
  await sendRequest({
    url,
    method: 'post',
    data: formData,
    thenFunction: async (res) => {
      if (res.status === 200) {
        const fileS3Data = res.data[0];
        const awsFormData = new FormData();
        const fields = Object.entries(fileS3Data.fields);
        fields.forEach((field) => {
          awsFormData.append(field[0], field[1]);
        });
        awsFormData.append('file', file);

        await sendRequest({
          url: fileS3Data.url,
          method: 'post',
          data: awsFormData,
          type: 'public',
          thenFunction: (res) => {
            if (res.status === 204) {
              setLoadingPresigned?.(false);
              key = fileS3Data.fields.key;
              toast.success(tnl(`labels.file_uploaded`));
            } else {
              toast.error(tnl(`labels.error_presigned_file`));
              setLoadingPresigned?.(false);
            }
          }
        });
      } else {
        toast.error(tnl(`labels.error_presigned_file`));
        setLoadingPresigned?.(false);
      }
    },
    catchFunction: () => {
      toast.error(tnl(`labels.error_presigned_file`));
      setLoadingPresigned?.(false);
    }
  });
  return key;
};

export {
  calculateAge,
  calculateColumnsWidth,
  cleanArray,
  cleanArrayOfObjects,
  cleanObject,
  createDropdownOptions,
  DATE_FORMATS,
  downloadServerFile,
  formatDate,
  formatDateForInput,
  getFormattedDefaultValue,
  newApplicationFromFrontend,
  redirectServerFile,
  turnIntoFormData,
  verifyData
};
export function isValidURL(text) {
  const pattern = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i;
  return pattern.test(text ?? '');
}

export { default as REGEX_OPTIONS } from './regexValidations';
export { default as sendRequest } from './sendRequest';

export * from './navigationHandler';
